import { audioFile } from './audioFile.js';

const DEBUGGING_ENABLED = false;

const audioContext = new (window.AudioContext || window.webkitAudioContext)();

const lookahead = 25.0; // How frequently to call scheduling function (in milliseconds)
const scheduleAheadTime = 0.1; // How far ahead to schedule audio (sec)

const SOUND_FOLDER_NAME = 'sounds/';
const SECTION_LENGTH = 16 * 2;

class Sequencer {
    gainNode = audioContext.createGain();
    isPlaying = false;
    preset = null;
    currentStep = 1;
    futureTickTime = 0.0;
    timerID = 0;

    currentSection = null;
    nextSection = null;
    cuedSection = null;

    nextStartTime = -1;
    lastStartTime = -1;

    cueInMiddle = false;

    constructor() {
        this.gainNode.gain.value = 1;
        this.gainNode.connect(audioContext.destination);
    }

    loadPreset(preset) {
        this.preset = preset;

        // TODO: Load audio files and only when finished loading, replace tracks
        this.tracks = preset.layers.map((fileName) =>
            audioFile(audioContext, SOUND_FOLDER_NAME + fileName, this.gainNode, preset.volume)
        );

        this.cueInMiddle = false;
    }

    stopAllTracks() {
        this.tracks.forEach((track) => track.stop());
    }

    stopAllTracksSmoothly() {
        const currentTime = audioContext.currentTime;

        // Fade out
        const fadeOutDuration = 1;
        this.gainNode.gain.setValueAtTime(1, audioContext.currentTime);
        this.gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + fadeOutDuration);

        // Stop all tracks after fade out, otherwise they would still play until this end of the loop cycle (problematic if user restarts immediately)
        this.tracks.forEach((track) => track.stop(audioContext.currentTime + fadeOutDuration));

        // Disable GUI until fade out has ended
        this.setDisabled(true);
        window.setTimeout(() => this.setDisabled(false), fadeOutDuration * 1000);
    }

    futureTick() {
        const secondsPerBeat = 60.0 / this.preset.tempo;
        this.futureTickTime += 0.25 * secondsPerBeat;
        this.currentStep++;

        if (this.cueInMiddle && this.currentStep > SECTION_LENGTH / 2) {
            this.currentStep = 1;
            this.cueInMiddle = false;
            DEBUGGING_ENABLED && console.log('cue in middle, jump to 1');
        }

        if (this.currentStep > SECTION_LENGTH) {
            this.currentStep = 1;
        }
    }

    scheduleTracks(step, startTime) {
        if (step === 1) {
            if (this.cuedSection) {
                this.nextSection = this.cuedSection;
                this.cuedSection = null;
            }

            if (!this.nextSection) return;

            const playStartOffset = this.getTimeBySection(this.nextSection);

            // Only play current loop sement of track (value depends on the length of the loop segments & tails)
            const stopTime = startTime + this.barsToTime(this.preset.numBars + this.preset.numBarsTail);

            this.tracks.forEach((track) => track.play(startTime, playStartOffset, stopTime));

            this.nextStartTime = startTime;
            this.lastStartTime = startTime;
        }
    }

    scheduler() {
        // While there are notes that will need to play before the next interval,
        // schedule them and advance the pointer.
        while (this.futureTickTime < audioContext.currentTime + scheduleAheadTime) {
            this.scheduleTracks(this.currentStep, this.futureTickTime);
            this.futureTick();
        }

        if (this.nextStartTime !== -1 && audioContext.currentTime >= this.nextStartTime) {
            const sectionHasChanged = this.currentSection !== this.nextSection;
            this.currentSection = this.nextSection;
            this.nextStartTime = -1;

            window.dispatchEvent(
                new CustomEvent('sectionStart', {
                    detail: {
                        newSection: this.currentSection,
                        sectionDuration: this.barsToTime(this.preset.numBars),
                    },
                })
            );

            if (sectionHasChanged) {
                window.dispatchEvent(
                    new CustomEvent('sectionChange', {
                        detail: {
                            newSection: this.currentSection,
                        },
                    })
                );
            }
        }

        this.timerID = setTimeout(this.scheduler.bind(this), lookahead);
    }

    seekToShorterSample() {
        const numBars = this.preset.numBars;
        const numBarsTail = this.preset.numBarsTail;

        const startTime = this.lastStartTime + this.barsToTime(numBars / 2);
        const playStartOffset = this.getTimeBySection(this.currentSection) + this.barsToTime(numBars * 1.5 + numBarsTail);
        const stopTime = this.lastStartTime + this.barsToTime(numBars / 2 + numBarsTail);

        this.tracks.forEach((track) =>
            track.seekToTime(startTime, playStartOffset, stopTime)
        );
    }

    cueSection(section) {
        this.cuedSection = section;

        if (this.nextSection && this.preset.supportsChangeInMiddle && this.preset.allowChangeInMiddle && this.currentStep < this.preset.numBars * 8) {
            this.seekToShorterSample();

            // TODO: Set to true to stop if the next section should not play (for better testing if the tail is good)
            if (false) {
                this.nextSection = null;
                this.cuedSection = null;
            }

            this.cueInMiddle = true;
        }
    }

    togglePlay() {
        this.isPlaying = !this.isPlaying;
        this.isPlaying ? this.play() : this.stop();
    }

    play() {
        this.gainNode.gain.setValueAtTime(1, audioContext.currentTime);
        this.isPlaying = true;

        this.currentStep = 1;
        this.futureTickTime = audioContext.currentTime;
        this.scheduler();
    }

    stop() {
        this.stopAllTracksSmoothly();
        this.isPlaying = false;

        this.nextSection = null;
        clearTimeout(this.timerID);
    }

    stopAfterCurrentSection() {
        this.seekToShorterSample();
        this.isPlaying = false;

        this.nextSection = null;
        clearTimeout(this.timerID);
    }

    setDisabled(disabled) {
        window.dispatchEvent(new CustomEvent('sequencerDisabledChange', { detail: disabled }));
    }

    barsToTime(bars) {
        return (60.0 / this.preset.tempo) * 0.25 * bars * 16;
    }

    getTimeBySection(section) {
        const numBars = this.preset.numBars;
        const numBarsTail = this.preset.numBarsTail;

        // TODO: Simplify formula
        const barsPerSection = this.preset.supportsChangeInMiddle ? numBars + numBarsTail + numBars / 2 + numBarsTail : numBars + numBarsTail;
        return this.barsToTime(this.preset.sections.indexOf(section) * barsPerSection);
    }
}

export default Sequencer;
